import { FC } from "react";
import { ProgramStructure2Data } from "./_schema";
import { cn } from "@/lib/utils";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

// Credits and Semester Tabs Component
export const CreditsAndSemesterTabs: FC<{
    parsedData: ProgramStructure2Data;
    semesters: any[];
    activeSemester: number;
    onSemesterChange: (index: number) => void;
}> = ({ parsedData, semesters, activeSemester, onSemesterChange }) => {
    const isAnimationEnabled =
        parsedData.enable_animation === true || parsedData.enable_animation === 1;

    return (
    <div className={cn(
        "flex flex-col gap-[16px] mb-[24px]",
        "sm:flex-row sm:items-center sm:justify-between sm:mb-[20px]",
        "md:mb-[28px]",
        "lg:mb-[28px]"
    )}>
        {/* Total Credits */}
        {parsedData.credits_text && parsedData.total_credits && (
            <div
                className={cn(
                    "flex items-center gap-[4px] order-1",
                    "text-[30px] tracking-[0.9px]",
                    "sm:order-2 sm:text-[22px] sm:tracking-[0.66px]",
                    "md:text-[25px] md:tracking-[0.75px]",
                    "lg:text-[30px] lg:tracking-[0.9px]",
                    "xl:text-[30px] xl:tracking-[0.9px]"
                )}
                style={{
                    fontFamily: "var(--font-merriweather)"
                }}
            >
            <RevealOnScroll once={true} y = {0} x={100} delay={100} enableAnimation={isAnimationEnabled}>

                <span
                    className="font-medium leading-[1.2]"
                    style={{ color: parsedData.credits_text_color }}
                >
                    {parsedData.credits_text}
                </span>
                <span
                    className="font-bold leading-[1.2]"
                    style={{ color: parsedData.total_credits_color }}
                >
                    {parsedData.total_credits}
                </span>
            </RevealOnScroll>
            </div>
        )}

        {/* Semester Tabs — animate the row as one unit so no individual pill is ever
            at opacity:0 when tapped. Per-pill RevealOnScroll with staggered delays
            caused pills 2+ to show active color only after their delay elapsed. */}
        <RevealOnScroll
            once={true}
            y={0}
            x={-50}
            enableAnimation={isAnimationEnabled}
            className="order-2 sm:order-1 min-w-0"
        >
            <div className={cn(
                "flex gap-[10px] overflow-x-auto scrollbar-hide",
                "sm:gap-[9px]",
                "md:gap-[10px]",
                "lg:gap-[12px]",
                "xl:gap-[12px]"
            )}>
                {semesters.map((semester, index) => (
                    <button
                        key={semester.id || index}
                        data-cta-name="semester_tab"
                        onClick={() => onSemesterChange(index)}
                        className={cn(
                            "shrink-0 flex items-center justify-center font-semibold leading-[1.49] whitespace-nowrap transition-all",
                            "px-[16px] py-[10px] rounded-[40px] text-[14px]",
                            "sm:px-[12px] sm:py-[8px] sm:rounded-[30px] sm:text-[12px]",
                            "md:px-[14px] md:py-[8px] md:rounded-[34px] md:text-[14px]",
                            "lg:px-[16px] lg:py-[10px] lg:rounded-[40px] lg:text-[16px]",
                            "xl:px-[16px] xl:py-[10px] xl:rounded-[40px] xl:text-[16px]"
                        )}
                        style={{
                            fontFamily: "var(--font-google-sans)",
                            ...(activeSemester === index ? {
                                backgroundColor: semester.active_color || parsedData.semester_active_bg_color || "#02a7b6",
                                color: semester.active_text_color || parsedData.semester_active_text_color || "#FFFFFF",
                                border: `1px solid ${semester.active_color || parsedData.semester_active_bg_color || "#02a7b6"}`,
                            } : {
                                backgroundColor: "transparent",
                                border: `0.6px solid ${semester.text_color || parsedData.semester_inactive_border_color || "rgba(255,255,255,0.4)"}`,
                                color: semester.text_color || parsedData.semester_inactive_text_color || "#FFFFFF",
                            })
                        }}
                    >
                        {semester.name}
                    </button>
                ))}
            </div>
        </RevealOnScroll>
    </div>
    );
};